const AUTH_TOKEN_STORAGE_PREFIX = "sb-";

const LOCAL_STORAGE_PRUNE_PREFIXES = [
  "mozzoapp:hero-image:",
  "mozzoapp:restaurant-profile:",
  "mozzoapp:restaurant-profile-state:",
  "mozzoapp:menu:",
  "mozzoapp:promotions:",
  "mozzoapp:promos:draft:",
  "mozzoapp:branch-tables:",
  "mozzoapp:qr-cart:",
  "mozzoapp:qr-submitted-order:",
  "mozzoapp:qr-customer:",
  "mozzoapp:kiosk-service-type:",
];

const SESSION_STORAGE_PRUNE_PREFIXES = [
  "mozzoapp:promos:draft-images:",
  "mozzoapp:promos:draft:",
  "mozzoapp:promotions:session:",
  "mozzoapp:payment-operations-session:",
  "mozzoapp:waiter-operations-session:",
  "mozzoapp:cash-register-session:",
  "mozzoapp:qr-cart:",
  "mozzoapp:qr-submitted-order:",
  "mozzoapp:qr-customer:",
  "mozzoapp:kiosk-service-type:",
];

const memoryStorage = new Map();

const isBrowser = () => typeof window !== "undefined";

const isQuotaExceededError = (error) => {
  if (!error) {
    return false;
  }

  const message = `${error.message || ""}`.toLowerCase();

  return (
    error.name === "QuotaExceededError" ||
    message.includes("quota") ||
    message.includes("exceeded")
  );
};

const getStorage = (storageType) => {
  if (!isBrowser()) {
    return null;
  }

  try {
    // Safari can allow reads while rejecting writes (quota or privacy settings).
    return storageType === "session" ? window.sessionStorage : window.localStorage;
  } catch (error) {
    return null;
  }
};

const getStorageEntrySize = (storage, key) => {
  try {
    const value = storage.getItem(key) || "";
    return key.length + value.length;
  } catch (error) {
    return 0;
  }
};

const removeMatchingKeys = (storage, prefixes, preservedKey) => {
  if (!storage) {
    return 0;
  }

  const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
    Boolean
  );

  const matchedKeys = keys
    .filter(
      (key) =>
        key !== preservedKey && prefixes.some((prefix) => key.startsWith(prefix))
    )
    .sort((leftKey, rightKey) => {
      const rightSize = getStorageEntrySize(storage, rightKey);
      const leftSize = getStorageEntrySize(storage, leftKey);
      return rightSize - leftSize;
    });

  let removedCount = 0;

  matchedKeys.forEach((key) => {
    try {
      storage.removeItem(key);
      removedCount += 1;
    } catch (error) {
      // Si una limpieza puntual falla, seguimos con el resto.
    }
  });

  return removedCount;
};

const freeStoragePressure = (storage, prefixes, preservedKey) => {
  const removedPreferred = removeMatchingKeys(storage, prefixes, preservedKey);

  if (removedPreferred > 0) {
    return removedPreferred;
  }

  return removeMatchingKeys(storage, ["mozzoapp:"], preservedKey);
};

const safeRemoveItem = (storage, key) => {
  if (!storage) {
    return false;
  }

  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    // No bloqueamos el flujo por no poder limpiar una copia vieja.
    return false;
  }
};

const isSupabaseAuthKey = (key) =>
  typeof key === "string" && key.startsWith(AUTH_TOKEN_STORAGE_PREFIX);

export const resilientSupabaseStorage = {
  getItem(key) {
    if (memoryStorage.has(key)) return memoryStorage.get(key);
    const sessionStorage = getStorage("session");
    const localStorage = getStorage("local");

    try {
      const sessionValue = sessionStorage?.getItem(key);
      if (sessionValue !== null && sessionValue !== undefined) {
        return sessionValue;
      }
    } catch (error) {
      // Seguimos con localStorage.
    }

    try {
      const localValue = localStorage?.getItem(key);
      if (localValue !== null && localValue !== undefined) {
        return localValue;
      }
    } catch (error) {
      // Seguimos con memoria.
    }

    return null;
  },

  setItem(key, value) {
    // Keep the newest value until both persistence and stale-copy cleanup succeed.
    memoryStorage.set(key, value);
    const localStorage = getStorage("local");
    const sessionStorage = getStorage("session");

    if (localStorage) {
      try {
        localStorage.setItem(key, value);
        if (safeRemoveItem(sessionStorage, key)) memoryStorage.delete(key);
        return;
      } catch (error) {
        if (isQuotaExceededError(error) && isSupabaseAuthKey(key)) {
          try {
            freeStoragePressure(localStorage, LOCAL_STORAGE_PRUNE_PREFIXES, key);
            localStorage.setItem(key, value);
            if (safeRemoveItem(sessionStorage, key)) memoryStorage.delete(key);
            return;
          } catch (retryError) {
            // Try session storage or memory when persistent storage is restricted.
          }
        }
      }
    }

    if (sessionStorage) {
      try {
        sessionStorage.setItem(key, value);
        if (safeRemoveItem(localStorage, key)) memoryStorage.delete(key);
        return;
      } catch (error) {
        if (isQuotaExceededError(error) && isSupabaseAuthKey(key)) {
          try {
            freeStoragePressure(sessionStorage, SESSION_STORAGE_PRUNE_PREFIXES, key);
            sessionStorage.setItem(key, value);
            if (safeRemoveItem(localStorage, key)) memoryStorage.delete(key);
            return;
          } catch (retryError) {
            // The in-memory copy remains usable during this page visit.
          }
        }
      }
    }

    console.warn(
      "No pudimos persistir la sesion de Supabase en el navegador. La dejamos activa solo en memoria para no frenar el acceso."
    );
  },

  removeItem(key) {
    const localRemoved = safeRemoveItem(getStorage("local"), key);
    const sessionRemoved = safeRemoveItem(getStorage("session"), key);
    if (localRemoved && sessionRemoved) memoryStorage.delete(key);
    else memoryStorage.set(key, null);
  },
};
